import React, { useRef } from 'react';
import { TextVariant } from '@naf/text';
import { ArticleSeries } from '../../../../../types/articleType';
import ArticleSeriesItem from '../ArticleSeriesItem/ArticleSeriesItem';
import { useScrollPosition } from './hooks/useScrollPosition';
import { useActiveArticleScroll } from './hooks/useActiveArticleScroll';
import { ScrollButton } from './components/ScrollButton';
import * as S from './Styles';

type Props = {
  articleSeries: ArticleSeries;
};

type ScrollDirection = 'left' | 'right';

const ArticleSeriesHeader = ({ articleSeries: { title, items } }: Props) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const activeRef = useRef<HTMLDivElement>(null);
  const { isScrolling, setIsScrolling, scrollPercent } = useScrollPosition(scrollRef);

  useActiveArticleScroll(activeRef, scrollRef);

  const handleScroll = (direction: ScrollDirection) => {
    if (isScrolling || !scrollRef.current) return;

    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
    const isAtStart = scrollLeft === 0;
    const isAtEnd = Math.ceil(scrollLeft) >= scrollWidth - clientWidth;

    if ((direction === 'left' && isAtStart) || (direction === 'right' && isAtEnd)) return;

    const carousel = scrollRef.current?.children[0];
    if (!carousel || !carousel.children.length) return;
    const card = carousel.children[0] as HTMLElement;
    const cardStyle = window.getComputedStyle(card);
    const cardMargin = parseFloat(cardStyle.marginRight);
    const scrollAmount = card.offsetWidth + cardMargin;

    setIsScrolling(true);
    scrollRef.current.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    });
  };

  return (
    <>
      <S.Header variant={TextVariant.Header3}>{title}</S.Header>
      <S.Wrapper>
        <ScrollButton
          direction="left"
          onClick={() => handleScroll('left')}
          isDisabled={isScrolling || scrollPercent === 0}
        />
        <S.ScrollContainer ref={scrollRef}>
          <S.ArticleCarousel>
            {items.map((article) => (
              <ArticleSeriesItem
                key={article.id}
                article={article}
                ref={article.isActive ? activeRef : null}
                showBorder
                layout="horizontal"
              />
            ))}
          </S.ArticleCarousel>
        </S.ScrollContainer>
        <ScrollButton
          direction="right"
          onClick={() => handleScroll('right')}
          isDisabled={isScrolling || scrollPercent === 1}
        />
      </S.Wrapper>
    </>
  );
};

export default ArticleSeriesHeader;
